import React from 'react';
import _ from 'lodash';
import hoistNonReactStatics from 'hoist-non-react-statics';

function getComponentName(component: React.ComponentType<any>) {
  return component.displayName ?? component.name;
}

/**
 * A React Higher Order Component.
 *
 * Simple example
 * ```
 * function withHocDisplayName(hocName: string): ReactHOC {
 *   return Component => _.assign(Component, { displayName: `${hocName}(${Component.displayName})` });
 * }
 * ```
 *
 * A more complicated example
 * ```
 * const withK: ReactHOC<{ k: boolean }> = Component =>
 * withHocDisplayName('withK')(props => <Component {...props} k={true} />)
 * ...
 * MyComponent: React.ComponentType<{k: boolean, j: boolean}
 *
 * const Wrapped = withK(MyComponent)
 * <Wrapped j={1}/> // this is a valid invocation of Wrapped
 * ```
 */
export interface ReactHOC<WrapperProps = {}, WrappedProps = {}> {
  <T extends WrappedProps>(
    Component: React.ComponentType<WrapperProps & T>,
  ): React.ComponentType<T>;
}

/**
 * Transforms a component into a component with a display name.
 * Untyped private function, do not export.
 */
const _withDisplayName = (displayName: string) => (C: any) => _.assign(C, { displayName });

/**
 * A curried function that assigns a `"withFoo(Inner)"` display name to a component
 *
 * Example:
 * ```
 * function InnerComponent() { return <div/> };
 * > withHocDisplayName('withFoo')(InnerComponent).displayName
 * "withFoo(Inner)""
 * ```
 * @param hocName
 */
export function withHocDisplayName(hocName: string): ReactHOC {
  return Component => _withDisplayName(`${hocName}(${getComponentName(Component)})`)(Component);
}

/**
 * A functional wrapper around React.forwardRef, binding props.forwardedRef to the passed in ref
 *
 * Note: withFR is missing PropsWithRef logic. So be careful here.
 * */
export const withFR: ReactHOC<{
  forwardedRef?: React.RefObject<any> | React.LegacyRef<any>;
}> = Component =>
  // @ts-ignore
  React.forwardRef(
    // @ts-ignore
    _withDisplayName('')((props, ref) => <Component {...props} forwardedRef={ref} />),
  );

/**
 * A functional wrapper around hoistNonReactStatics.
 * Legacy code, keep it in case it matters.
 */
export function withNonReactStatics(WrappedComponent: any): ReactHOC {
  return Component => hoistNonReactStatics(Component, WrappedComponent);
}
